import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { receivePurchaseRpc, ReceivePurchasePayload } from '@/services/purchases';
import { createSupplier, fetchAllActiveSuppliers } from '@/services/suppliers';
import { fetchAllActiveAccessories } from '@/services/accessories';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Modal } from '@/components/common/Modal';
import { SupplierForm } from '@/features/suppliers/SupplierForm';
import { LaptopSpecExtractorModal } from '@/features/ai/LaptopSpecExtractorModal';
import {
  ExtractedInvoiceItem,
  ExtractedLaptopSpecs,
  findExactSupplierMatch,
  normalizeSupplierName,
  classifyInvoiceItem,
  canonicalizeLaptopModel,
} from '@/services/aiExtraction';
import { ExtractedSpreadsheetPurchase } from '@/services/purchaseSpreadsheet';
import { computeLaptopGroupKey } from '@/services/purchaseSpreadsheetHelpers';
import {
  Plus,
  Trash2,
  Package,
  Save,
  Loader2,
  Sparkles,
  Building2,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Layers,
  Barcode,
} from 'lucide-react';

const unitSlotSchema = z.object({
  serial_number: z.string().optional().or(z.literal('')),
  service_tag: z.string().optional().or(z.literal('')),
  barcode: z.string().optional().or(z.literal('')),
});

const laptopBatchSchema = z.object({
  type: z.literal('laptop_batch'),
  brand: z.string().min(1, 'Brand required'),
  model: z.string().min(1, 'Model required'),
  raw_description: z.string().optional().or(z.literal('')),
  cpu: z.string().optional().or(z.literal('')),
  ram: z.string().optional().or(z.literal('')),
  storage: z.string().optional().or(z.literal('')),
  gpu: z.string().optional().or(z.literal('')),
  screen_size: z.string().optional().or(z.literal('')),
  touchscreen: z.boolean().nullable().optional(),
  resolution: z.string().optional().or(z.literal('')),
  colour: z.string().optional().or(z.literal('')),
  keyboard_layout: z.string().optional().or(z.literal('')),
  operating_system: z.string().optional().or(z.literal('')),
  condition: z.enum(['new', 'excellent', 'good', 'fair', 'poor']).default('good'),
  grade: z.string().optional().or(z.literal('')),
  battery_health: z.coerce.number().min(0).max(100).optional().or(z.literal('')),
  charger_included: z.boolean().optional().default(false),
  charger_notes: z.string().optional().or(z.literal('')),
  accessories_included: z.string().optional().or(z.literal('')),
  refurbishment_cost: z.coerce.number().min(0).optional().default(0),
  other_cost: z.coerce.number().min(0).optional().default(0),
  warranty_months: z.coerce.number().int().min(0).optional().default(1),
  description: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  is_public: z.boolean().optional().default(true),
  quantity: z.coerce.number().int().min(1, 'Quantity must be at least 1'),
  unit_cost: z.coerce.number().min(1, 'Unit cost required'),
  selling_price: z.coerce.number().min(0).default(0),
  minimum_price: z.coerce.number().optional().or(z.literal('')),
  units: z.array(unitSlotSchema).default([]),
});

const existingAccessoryLineSchema = z.object({
  type: z.literal('existing_accessory'),
  accessory_id: z.string().min(1, 'Select an accessory'),
  quantity: z.coerce.number().min(1, 'Qty must be >= 1'),
  unit_cost: z.coerce.number().min(1, 'Cost required'),
});

const newAccessoryLineSchema = z.object({
  type: z.literal('new_accessory'),
  name: z.string().min(1, 'Name required'),
  category: z.string().min(1, 'Category required'),
  sku: z.string().optional().or(z.literal('')),
  barcode: z.string().optional().or(z.literal('')),
  brand: z.string().optional().or(z.literal('')),
  quantity: z.coerce.number().min(1, 'Qty must be >= 1'),
  unit_cost: z.coerce.number().min(1, 'Cost required'),
  selling_price: z.coerce.number().min(0).default(0),
  reorder_level: z.coerce.number().default(5),
  description: z.string().optional().or(z.literal('')),
  is_public: z.boolean().optional().default(false),
});

const purchaseFormSchema = z.object({
  supplier_id: z.string().min(1, 'Supplier is required'),
  invoice_reference: z.string().optional(),
  purchase_date: z.string().min(1, 'Purchase date required'),
  shipping_cost: z.coerce.number().min(0).default(0),
  other_cost: z.coerce.number().min(0).default(0),
  discount: z.coerce.number().min(0).default(0),
  notes: z.string().optional(),
  amount_paid: z.coerce.number().min(0).default(0),
  payment_method: z.enum(['cash', 'bank_transfer', 'mobile_wallet', 'other']).default('cash'),
  laptop_batches: z.array(laptopBatchSchema).default([]),
  existing_accessory_items: z.array(existingAccessoryLineSchema).default([]),
  new_accessory_items: z.array(newAccessoryLineSchema).default([]),
});

type PurchaseFormData = z.infer<typeof purchaseFormSchema>;

export interface ReceivePurchasePrefill {
  invoice_reference?: string;
  purchase_date?: string;
  shipping_cost?: number;
  other_cost?: number;
  discount?: number;
  notes?: string;
  /** Amount paid, preserved from spreadsheet extraction — never reset to 0 */
  amount_paid?: number;
  /** Payment method, preserved from spreadsheet extraction — never reset to cash */
  payment_method?: 'cash' | 'bank_transfer' | 'mobile_wallet' | 'other';
  /** Informational only — AI-detected supplier name for user reference */
  extracted_supplier_name?: string | null;
  /** Extracted line items from invoice */
  extracted_items?: ExtractedInvoiceItem[];
  /** Structured spreadsheet import draft */
  extracted_draft?: ExtractedSpreadsheetPurchase | null;
}

interface ReceivePurchaseFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  /** Pre-fill header fields from AI invoice extraction. No automatic actions. */
  initialData?: ReceivePurchasePrefill;
}

export const ReceivePurchaseForm: React.FC<ReceivePurchaseFormProps> = ({
  onSuccess,
  onCancel,
  initialData,
}) => {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [quickSupplierError, setQuickSupplierError] = useState<string | null>(null);
  const [showNewSupplierModal, setShowNewSupplierModal] = useState(false);
  const [activeScanTarget, setActiveScanTarget] = useState<{ batchIdx: number; unitIdx: number } | null>(null);
  const [expandedBatches, setExpandedBatches] = useState<Record<number, boolean>>({});
  const [batchWarnings, setBatchWarnings] = useState<Record<number, string>>({});
  const [aiItemsApplied, setAiItemsApplied] = useState(false);
  const hasAutoMatchedSupplier = useRef(false);
  const hasAutoAppliedItems = useRef(false);

  const { data: suppliersData, isPending: suppliersLoading, error: suppliersError, refetch: retrySuppliers } = useQuery({
    queryKey: ['suppliers', 'all-active'],
    queryFn: async () => {
      const all = await fetchAllActiveSuppliers();
      return { data: all, count: all.length };
    },
  });

  const { data: accessoriesData, error: accessoriesError, refetch: retryAccessories } = useQuery({
    queryKey: ['accessories', 'all-active'],
    queryFn: async () => {
      const all = await fetchAllActiveAccessories();
      return { data: all, count: all.length };
    },
  });

  // Query existing laptop brands and models for canonicalization
  const { data: existingLaptopsData } = useQuery({
    queryKey: ['laptops', 'brand-models-canonical'],
    queryFn: async () => {
      const { data, error } = await supabase.from('laptops').select('brand, model').limit(500);
      if (error) return [];
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<PurchaseFormData>({
    resolver: zodResolver(purchaseFormSchema),
    defaultValues: {
      supplier_id: '',
      invoice_reference: initialData?.invoice_reference ?? '',
      purchase_date: initialData?.purchase_date ?? new Date().toISOString().slice(0, 10),
      shipping_cost: initialData?.shipping_cost ?? 0,
      other_cost: initialData?.other_cost ?? 0,
      discount: initialData?.discount ?? 0,
      notes: initialData?.notes ?? '',
      amount_paid: initialData?.amount_paid ?? 0,
      payment_method: initialData?.payment_method ?? 'cash',
      laptop_batches: [],
      existing_accessory_items: [],
      new_accessory_items: [],
    },
  });

  const laptopBatchesFieldArray = useFieldArray({ control, name: 'laptop_batches' });
  const existingAccFieldArray = useFieldArray({ control, name: 'existing_accessory_items' });
  const newAccFieldArray = useFieldArray({ control, name: 'new_accessory_items' });

  const watchSupplierId = watch('supplier_id');
  const watchBatches = watch('laptop_batches') || [];
  const watchExistingAcc = watch('existing_accessory_items') || [];
  const watchNewAcc = watch('new_accessory_items') || [];
  const watchShipping = Number(watch('shipping_cost')) || 0;
  const watchOther = Number(watch('other_cost')) || 0;
  const watchDiscount = Number(watch('discount')) || 0;
  const watchAmountPaid = Number(watch('amount_paid')) || 0;

  // ── Deterministic Live Totals ───────────────────────────────────────────────
  // Laptop batch line total = quantity * unit_cost
  const laptopsSubtotal = watchBatches.reduce((acc, b) => {
    const qty = Number(b.quantity) || 0;
    const cost = Number(b.unit_cost) || 0;
    return acc + qty * cost;
  }, 0);

  const existingAccSubtotal = watchExistingAcc.reduce(
    (acc, a) => acc + (Number(a.unit_cost) || 0) * (Number(a.quantity) || 1),
    0
  );
  const newAccSubtotal = watchNewAcc.reduce(
    (acc, a) => acc + (Number(a.unit_cost) || 0) * (Number(a.quantity) || 1),
    0
  );

  const subtotal = laptopsSubtotal + existingAccSubtotal + newAccSubtotal;
  const totalExtraCosts = watchShipping + watchOther;
  const netPurchaseTotal = Math.max(0, subtotal + totalExtraCosts - watchDiscount);
  const balanceDue = Math.max(0, netPurchaseTotal - watchAmountPaid);

  // ── 1. Supplier Matching Logic ──────────────────────────────────────────────
  const matchedSupplier = React.useMemo(() => {
    if (!initialData?.extracted_supplier_name || !suppliersData?.data) return null;
    return findExactSupplierMatch(initialData.extracted_supplier_name, suppliersData.data);
  }, [initialData?.extracted_supplier_name, suppliersData?.data]);

  // Auto-select exact matched supplier once
  useEffect(() => {
    if (matchedSupplier && !watchSupplierId && !hasAutoMatchedSupplier.current) {
      hasAutoMatchedSupplier.current = true;
      setValue('supplier_id', matchedSupplier.id, { shouldValidate: true, shouldDirty: true });
    }
  }, [matchedSupplier, watchSupplierId, setValue]);

  // Quick Create Supplier Mutation
  const quickCreateSupplierMutation = useMutation({
    mutationFn: async (name: string) => {
      if (suppliersLoading || suppliersError) throw new Error('Load the supplier list before creating a supplier. Use Retry above.');
      const cleanName = name.trim();
      if (!cleanName) throw new Error('Supplier name is required');

      if (suppliersData?.data) {
        const norm = normalizeSupplierName(cleanName);
        const existing = suppliersData.data.find((s) => normalizeSupplierName(s.name) === norm);
        if (existing) {
          return existing;
        }
      }

      return createSupplier({ name: cleanName });
    },
    onSuccess: (newSup) => {
      setQuickSupplierError(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      setValue('supplier_id', newSup.id, { shouldValidate: true, shouldDirty: true });
    },
    onError: (err: unknown) => {
      setQuickSupplierError(parseDatabaseError(err));
    },
  });

  // ── 2. AI Items / Spreadsheet Draft -> Batch UI Rows Conversion ───────────
  const convertAiItemsToBatches = (items: ExtractedInvoiceItem[]) => {
    const existingLaptops = existingLaptopsData || [];
    const newBatches: z.infer<typeof laptopBatchSchema>[] = [];
    const newExistingAcc: z.infer<typeof existingAccessoryLineSchema>[] = [];
    const newAccessories: z.infer<typeof newAccessoryLineSchema>[] = [];

    for (const item of items) {
      const classification = classifyInvoiceItem(item.description);

      if (classification.type === 'laptop') {
        const qty = Math.max(1, Math.round(item.quantity));
        const specs = classification.laptopSpecs;

        const rawBrand = specs?.brand || 'Laptop';
        const rawModel = specs?.model || item.description;

        // Canonicalize spelling/case against existing inventory models
        const canonical = canonicalizeLaptopModel(rawBrand, rawModel, existingLaptops);

        // Pre-fill unit slots according to batch quantity
        const unitSlots = Array.from({ length: qty }, () => ({
          serial_number: '',
          service_tag: '',
          barcode: '',
        }));

        newBatches.push({
          type: 'laptop_batch',
          brand: canonical.brand,
          model: canonical.model,
          raw_description: item.description,
          cpu: specs?.cpu || '',
          ram: specs?.ram || '',
          storage: specs?.storage || '',
          gpu: '',
          colour: '',
          condition: 'good',
          battery_health: '',
          charger_included: false,
          refurbishment_cost: 0,
          other_cost: 0,
          warranty_months: 1,
          is_public: true,
          quantity: qty,
          unit_cost: item.unit_cost,
          selling_price: (item as any).selling_price > 0 ? (item as any).selling_price : 0, // Never manufactured
          minimum_price: (item as any).minimum_price || '',
          units: unitSlots,
        });
      } else if (classification.type === 'accessory') {
        const accInfo = classification.accessoryInfo;
        const normDesc = normalizeSupplierName(item.description);

        // Safe Accessory Matching: Exact normalized SKU match only
        const matchedAcc = accessoriesData?.data?.find(
          (a) => a.sku && a.sku.trim().toUpperCase() === normDesc.toUpperCase()
        );

        if (matchedAcc) {
          newExistingAcc.push({
            type: 'existing_accessory',
            accessory_id: matchedAcc.id,
            quantity: Math.max(1, Math.round(item.quantity)),
            unit_cost: item.unit_cost,
          });
        } else {
          newAccessories.push({
            type: 'new_accessory',
            name: accInfo?.name || item.description,
            category: accInfo?.category || 'Other Accessories',
            sku: '',
            quantity: Math.max(1, Math.round(item.quantity)),
            unit_cost: item.unit_cost,
            selling_price: (item as any).selling_price > 0 ? (item as any).selling_price : 0,
            reorder_level: 5,
            is_public: false,
          });
        }
      } else {
        // Unknown: treat as New Accessory draft
        newAccessories.push({
          type: 'new_accessory',
          name: item.description,
          category: 'Other Accessories',
          sku: '',
          quantity: Math.max(1, Math.round(item.quantity)),
          unit_cost: item.unit_cost,
          selling_price: (item as any).selling_price > 0 ? (item as any).selling_price : 0,
          reorder_level: 5,
          is_public: false,
        });
      }
    }

    if (newBatches.length > 0) laptopBatchesFieldArray.replace(newBatches);
    if (newExistingAcc.length > 0) existingAccFieldArray.replace(newExistingAcc);
    if (newAccessories.length > 0) newAccFieldArray.replace(newAccessories);

    setAiItemsApplied(true);
  };

  const convertSpreadsheetDraftToBatches = (draft: ExtractedSpreadsheetPurchase) => {
    const newBatches: z.infer<typeof laptopBatchSchema>[] = [];
    const newExistingAcc: z.infer<typeof existingAccessoryLineSchema>[] = [];
    const newAccessories: z.infer<typeof newAccessoryLineSchema>[] = [];

    // Group laptops by identical specifications & price
    const laptopGroupMap = new Map<string, {
      brand: string;
      model: string;
      raw_description: string;
      cpu: string;
      ram: string;
      storage: string;
      gpu: string;
      screen_size: string;
      touchscreen: boolean | null;
      resolution: string;
      colour: string;
      keyboard_layout: string;
      operating_system: string;
      condition: 'new' | 'excellent' | 'good' | 'fair' | 'poor';
      grade: string;
      battery_health: number | '';
      charger_included: boolean;
      charger_notes: string;
      accessories_included: string;
      refurbishment_cost: number;
      other_cost: number;
      warranty_months: number;
      description: string;
      notes: string;
      is_public: boolean;
      unit_cost: number;
      selling_price: number;
      minimum_price: number | '';
      units: Array<{ serial_number: string; service_tag: string; barcode: string }>;
    }>();

    for (const item of draft.items) {
      if (item.item_type === 'laptop') {
        const groupKey = computeLaptopGroupKey(item);

        const existingGroup = laptopGroupMap.get(groupKey);
        const unitSlot = {
          serial_number: item.serial_number || '',
          service_tag: item.service_tag || '',
          barcode: item.barcode || '',
        };

        if (existingGroup) {
          existingGroup.units.push(unitSlot);
        } else {
          laptopGroupMap.set(groupKey, {
            brand: item.brand || 'Laptop',
            model: item.model || 'Unknown Model',
            raw_description: item.original_description || `${item.brand} ${item.model}`,
            cpu: item.cpu || '',
            ram: item.ram || '',
            storage: item.storage || '',
            gpu: item.gpu || '',
            screen_size: item.screen_size || '',
            touchscreen: item.touchscreen ?? null,
            resolution: item.resolution || '',
            colour: item.colour || '',
            keyboard_layout: item.keyboard_layout || 'US',
            operating_system: item.operating_system || '',
            condition: item.condition || 'good',
            grade: item.grade || '',
            battery_health: item.battery_health ?? '',
            charger_included: item.charger_included ?? true,
            charger_notes: item.charger_notes || '',
            accessories_included: item.accessories_included || '',
            refurbishment_cost: item.refurbishment_cost || 0,
            other_cost: item.other_cost || 0,
            warranty_months: item.warranty_months ?? 1,
            description: item.description || '',
            notes: item.notes || '',
            is_public: item.is_public ?? true,
            unit_cost: item.unit_cost,
            selling_price: item.selling_price || 0,
            minimum_price: item.minimum_price || '',
            units: [unitSlot],
          });
        }
      } else if (item.item_type === 'accessory' || item.item_type === 'new_accessory') {
        // Safe accessory matching: Exact normalized SKU match only
        if (item.exact_sku_matched && item.accessory_id) {
          newExistingAcc.push({
            type: 'existing_accessory',
            accessory_id: item.accessory_id,
            quantity: Math.max(1, item.quantity || 1),
            unit_cost: item.unit_cost,
          });
        } else {
          newAccessories.push({
            type: 'new_accessory',
            name: item.accessory_name || item.original_description || 'Accessory',
            category: item.accessory_category || 'Other Accessories',
            sku: item.accessory_sku || '',
            barcode: item.accessory_barcode || '',
            brand: item.accessory_brand || '',
            quantity: Math.max(1, item.quantity || 1),
            unit_cost: item.unit_cost,
            selling_price: item.selling_price || 0,
            reorder_level: item.reorder_level || 5,
            description: item.description || '',
            is_public: item.is_public ?? false,
          });
        }
      }
    }

    for (const group of laptopGroupMap.values()) {
      newBatches.push({
        type: 'laptop_batch',
        brand: group.brand,
        model: group.model,
        raw_description: group.raw_description,
        cpu: group.cpu,
        ram: group.ram,
        storage: group.storage,
        gpu: group.gpu,
        screen_size: group.screen_size,
        touchscreen: group.touchscreen,
        resolution: group.resolution,
        colour: group.colour,
        keyboard_layout: group.keyboard_layout,
        operating_system: group.operating_system,
        condition: group.condition,
        grade: group.grade,
        battery_health: group.battery_health,
        charger_included: group.charger_included,
        charger_notes: group.charger_notes,
        accessories_included: group.accessories_included,
        refurbishment_cost: group.refurbishment_cost,
        other_cost: group.other_cost,
        warranty_months: group.warranty_months,
        description: group.description,
        notes: group.notes,
        is_public: group.is_public,
        quantity: group.units.length,
        unit_cost: group.unit_cost,
        selling_price: group.selling_price,
        minimum_price: group.minimum_price,
        units: group.units,
      });
    }

    laptopBatchesFieldArray.replace(newBatches);
    existingAccFieldArray.replace(newExistingAcc);
    newAccFieldArray.replace(newAccessories);

    setAiItemsApplied(true);
  };

  // Auto-apply AI / spreadsheet items on mount once
  useEffect(() => {
    if (initialData?.extracted_draft && !hasAutoAppliedItems.current) {
      hasAutoAppliedItems.current = true;
      convertSpreadsheetDraftToBatches(initialData.extracted_draft);
    } else if (
      initialData?.extracted_items &&
      initialData.extracted_items.length > 0 &&
      !hasAutoAppliedItems.current
    ) {
      hasAutoAppliedItems.current = true;
      convertAiItemsToBatches(initialData.extracted_items);
    }
  }, [initialData?.extracted_draft, initialData?.extracted_items, existingLaptopsData]);

  // ── 3. Handle Quantity Change for a Batch ──────────────────────────────────
  const handleQuantityChange = (batchIdx: number, newQtyStr: string) => {
    const newQty = Math.max(1, parseInt(newQtyStr, 10) || 1);
    setValue(`laptop_batches.${batchIdx}.quantity`, newQty, { shouldDirty: true, shouldValidate: true });

    const currentUnits = getValues(`laptop_batches.${batchIdx}.units`) || [];
    if (newQty > currentUnits.length) {
      const added = Array.from({ length: newQty - currentUnits.length }, () => ({
        serial_number: '',
        service_tag: '',
        barcode: '',
      }));
      setValue(`laptop_batches.${batchIdx}.units`, [...currentUnits, ...added], { shouldDirty: true });
    } else if (newQty < currentUnits.length) {
      // Check if removed units had data
      const removedUnits = currentUnits.slice(newQty);
      const hadData = removedUnits.some((u) => u.serial_number || u.service_tag || u.barcode);
      if (hadData) {
        setBatchWarnings((prev) => ({
          ...prev,
          [batchIdx]: `Quantity reduced: Removed ${removedUnits.length} unit slot(s) that contained serial data.`,
        }));
      }
      setValue(`laptop_batches.${batchIdx}.units`, currentUnits.slice(0, newQty), { shouldDirty: true });
    }
  };

  const toggleBatchExpand = (idx: number) => {
    setExpandedBatches((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // ── 4. Unit Spec Scan Confirmed ─────────────────────────────────────────────
  const handleUnitSpecConfirmed = (specs: ExtractedLaptopSpecs) => {
    if (!activeScanTarget) return;
    const { batchIdx, unitIdx } = activeScanTarget;

    // 1. Populate unit-level serials
    if (specs.serial_number) {
      setValue(`laptop_batches.${batchIdx}.units.${unitIdx}.serial_number`, specs.serial_number, { shouldDirty: true });
    }
    if (specs.service_tag) {
      setValue(`laptop_batches.${batchIdx}.units.${unitIdx}.service_tag`, specs.service_tag, { shouldDirty: true });
    }

    // 2. Fill empty batch specs
    const currentBatch = getValues(`laptop_batches.${batchIdx}`);
    const conflicts: string[] = [];

    if (!currentBatch.cpu && specs.cpu) {
      setValue(`laptop_batches.${batchIdx}.cpu`, specs.cpu, { shouldDirty: true });
    } else if (currentBatch.cpu && specs.cpu && currentBatch.cpu.toLowerCase() !== specs.cpu.toLowerCase()) {
      conflicts.push(`Scanned CPU (${specs.cpu}) differs from batch (${currentBatch.cpu})`);
    }

    if (!currentBatch.ram && specs.ram) {
      setValue(`laptop_batches.${batchIdx}.ram`, specs.ram, { shouldDirty: true });
    } else if (currentBatch.ram && specs.ram && currentBatch.ram.toLowerCase() !== specs.ram.toLowerCase()) {
      conflicts.push(`Scanned RAM (${specs.ram}) differs from batch (${currentBatch.ram})`);
    }

    if (!currentBatch.storage && specs.storage) {
      setValue(`laptop_batches.${batchIdx}.storage`, specs.storage, { shouldDirty: true });
    } else if (currentBatch.storage && specs.storage && currentBatch.storage.toLowerCase() !== specs.storage.toLowerCase()) {
      conflicts.push(`Scanned Storage (${specs.storage}) differs from batch (${currentBatch.storage})`);
    }

    if (!currentBatch.colour && specs.colour) {
      setValue(`laptop_batches.${batchIdx}.colour`, specs.colour, { shouldDirty: true });
    }

    if (conflicts.length > 0) {
      setBatchWarnings((prev) => ({
        ...prev,
        [batchIdx]: `Unit #${unitIdx + 1} Spec Conflict: ${conflicts.join(', ')}. Batch values were kept.`,
      }));
    }

    setActiveScanTarget(null);
  };

  // ── 5. Main Purchase RPC Mutation ──────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: async (data: PurchaseFormData) => {
      // Expand batch quantities into individual serialized laptop objects for the RPC contract
      if (suppliersError || suppliersLoading) throw new Error('The supplier list has not loaded. Use Retry before receiving this purchase.');
      const serializedLaptops: ReceivePurchasePayload['items'] = data.laptop_batches.flatMap((batch) => {
        const qty = Math.max(1, Math.round(batch.quantity));
        const items = [];

        for (let i = 0; i < qty; i++) {
          const unit = batch.units?.[i] || {};
          items.push({
            type: 'laptop' as const,
            brand: batch.brand,
            model: batch.model,
            cpu: batch.cpu || null,
            ram: batch.ram || null,
            storage: batch.storage || null,
            gpu: batch.gpu || null,
            screen_size: batch.screen_size || null,
            touchscreen: batch.touchscreen ?? null,
            resolution: batch.resolution || null,
            colour: batch.colour || null,
            keyboard_layout: batch.keyboard_layout || null,
            operating_system: batch.operating_system || null,
            condition: batch.condition,
            grade: batch.grade || null,
            battery_health: typeof batch.battery_health === 'number' ? batch.battery_health : null,
            charger_included: batch.charger_included,
            charger_notes: batch.charger_notes || null,
            accessories_included: batch.accessories_included || null,
            refurbishment_cost: batch.refurbishment_cost || 0,
            other_cost: batch.other_cost || 0,
            selling_price: batch.selling_price,
            minimum_price: typeof batch.minimum_price === 'number' ? batch.minimum_price : null,
            warranty_months: batch.warranty_months,
            description: batch.description || null,
            notes: batch.notes || null,
            is_public: batch.is_public,
            unit_cost: batch.unit_cost,
            serial_number: unit.serial_number ? unit.serial_number.trim() : null,
            service_tag: unit.service_tag ? unit.service_tag.trim() : null,
            barcode: unit.barcode ? unit.barcode.trim() : null,
          });
        }
        return items;
      });

      const allItems: ReceivePurchasePayload['items'] = [
        ...serializedLaptops,
        ...data.existing_accessory_items.map((a) => ({
          type: 'accessory' as const,
          accessory_id: a.accessory_id,
          quantity: a.quantity,
          unit_cost: a.unit_cost,
        })),
        ...data.new_accessory_items.map((a) => ({
          type: 'accessory' as const,
          name: a.name,
          category: a.category,
          sku: a.sku || null,
          barcode: a.barcode || null,
          brand: a.brand || null,
          quantity: a.quantity,
          unit_cost: a.unit_cost,
          selling_price: a.selling_price,
          reorder_level: a.reorder_level,
          description: a.description || null,
          is_public: a.is_public,
        })),
      ];

      if (allItems.length === 0) {
        throw new Error('Please add at least one laptop or accessory item to this purchase.');
      }

      await receivePurchaseRpc({
        supplier_id: data.supplier_id,
        reference: data.invoice_reference || null,
        purchase_date: data.purchase_date,
        shipping_cost: data.shipping_cost,
        other_cost: data.other_cost,
        discount: data.discount,
        notes: data.notes || null,
        amount_paid: data.amount_paid,
        payment_method: data.payment_method,
        items: allItems,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      onSuccess();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const onSubmit = (data: PurchaseFormData) => {
    setSubmitError(null);
    mutation.mutate(data);
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-3 py-2 bg-brand-bg border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green transition-all ${
      hasError ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
    }`;

  const selectedSupplierObj = suppliersData?.data?.find((s) => s.id === watchSupplierId);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, () => setSubmitError('Please check the required fields: supplier, item names, quantities and purchase costs.'))} className="space-y-4 max-h-[80vh] overflow-y-auto pr-1" noValidate>
        {submitError && <ErrorAlert message={submitError} />}
        {suppliersError && <ErrorAlert message={`Unable to load suppliers: ${parseDatabaseError(suppliersError)}`} onRetry={() => { void retrySuppliers(); }} />}
        {accessoriesError && <ErrorAlert message={`Unable to load accessories: ${parseDatabaseError(accessoriesError)}`} onRetry={() => { void retryAccessories(); }} />}
        {quickSupplierError && <ErrorAlert message={quickSupplierError} />}

        {/* ── AI Extraction Handoff Banner ─────────────────────────────────── */}
        {initialData && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1.5 font-bold text-green-900">
                <Sparkles className="w-4 h-4 text-brand-green" />
                <span>AI-Extracted Invoice Draft</span>
              </div>
              {initialData.extracted_items && initialData.extracted_items.length > 0 && (
                <button
                  type="button"
                  onClick={() => convertAiItemsToBatches(initialData.extracted_items!)}
                  className="inline-flex items-center space-x-1 px-2.5 py-1 bg-white border border-green-300 text-green-800 hover:bg-green-100 rounded text-[11px] font-semibold transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Re-apply AI Batches</span>
                </button>
              )}
            </div>

            <p className="text-[11px] text-green-700">
              Reference, dates, shipping, and batched purchase rows have been populated for review. Review every item before confirming.
            </p>

            {/* Supplier Resolution Section */}
            {initialData.extracted_supplier_name && (
              <div className="p-2.5 bg-white/80 rounded border border-green-200 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                  <div className="text-[11px]">
                    <span className="text-brand-muted">Detected Supplier: </span>
                    <strong className="text-brand-black">{initialData.extracted_supplier_name}</strong>
                  </div>

                  {matchedSupplier ? (
                    <span className="inline-flex items-center space-x-1 text-[11px] text-brand-green font-bold bg-green-100 px-2 py-0.5 rounded">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Exact match auto-selected ({matchedSupplier.name})</span>
                    </span>
                  ) : selectedSupplierObj ? (
                    <span className="inline-flex items-center space-x-1 text-[11px] text-blue-700 font-bold bg-blue-50 px-2 py-0.5 rounded">
                      <Building2 className="w-3.5 h-3.5" />
                      <span>Selected: {selectedSupplierObj.name}</span>
                    </span>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => quickCreateSupplierMutation.mutate(initialData.extracted_supplier_name!)}
                        disabled={quickCreateSupplierMutation.isPending}
                        className="inline-flex items-center space-x-1 px-2.5 py-1 bg-brand-green text-white rounded text-[11px] font-bold hover:bg-brand-green-hover disabled:opacity-50"
                      >
                        {quickCreateSupplierMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        <span>Create &quot;{initialData.extracted_supplier_name}&quot;</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowNewSupplierModal(true)}
                        disabled={suppliersLoading || !!suppliersError}
                        className="inline-flex items-center space-x-1 px-2 py-1 bg-white border border-brand-border text-brand-black hover:bg-brand-bg rounded text-[11px] font-semibold"
                      >
                        <span>+ Custom Supplier</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Header Info ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-brand-bg p-3 rounded-lg border border-brand-border">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider">
                Supplier <span className="text-brand-red">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowNewSupplierModal(true)}
                disabled={suppliersLoading || !!suppliersError}
                className="text-[10px] text-brand-green font-bold hover:underline"
              >
                + New Supplier
              </button>
            </div>
            <select disabled={suppliersLoading || !!suppliersError} {...register('supplier_id')} className={inputClass(!!errors.supplier_id)}>
              <option value="">{suppliersLoading ? 'Loading suppliers…' : suppliersError ? 'Supplier list unavailable' : 'Select Supplier'}</option>
              {suppliersData?.data.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.phone ? ` — ${s.phone}` : ''}
                </option>
              ))}
            </select>
            {errors.supplier_id && <p className="mt-1 text-xs text-brand-red">{errors.supplier_id.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Invoice / Ref Number
            </label>
            <input {...register('invoice_reference')} placeholder="INV-2026-089" className={inputClass()} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Purchase Date <span className="text-brand-red">*</span>
            </label>
            <input {...register('purchase_date')} type="date" className={inputClass()} />
          </div>
        </div>

        {/* ── Line Items Section ──────────────────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-brand-black">Line Items</h4>
              {aiItemsApplied && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                  AI Batches Applied — Edit quantities, serials, and prices before receiving
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() =>
                  laptopBatchesFieldArray.append({
                    type: 'laptop_batch',
                    brand: '',
                    model: '',
                    raw_description: '',
                    cpu: '',
                    ram: '',
                    storage: '',
                    gpu: '',
                    colour: '',
                    condition: 'good',
                    battery_health: '',
                    charger_included: false,
                    refurbishment_cost: 0,
                    other_cost: 0,
                    warranty_months: 1,
                    is_public: true,
                    quantity: 1,
                    unit_cost: 0,
                    selling_price: 0,
                    minimum_price: '',
                    units: [{ serial_number: '', service_tag: '', barcode: '' }],
                  })
                }
                className="inline-flex items-center space-x-1 px-2.5 py-1 bg-brand-green/10 text-brand-green border border-brand-green/30 rounded text-xs font-semibold hover:bg-brand-green/20"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Laptop Batch</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  existingAccFieldArray.append({
                    type: 'existing_accessory',
                    accessory_id: '',
                    quantity: 1,
                    unit_cost: 0,
                  })
                }
                className="inline-flex items-center space-x-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-semibold hover:bg-blue-100"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Existing Accessory</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  newAccFieldArray.append({
                    type: 'new_accessory',
                    name: '',
                    category: 'Charger & Power',
                    sku: '',
                    quantity: 1,
                    unit_cost: 0,
                    selling_price: 0,
                    reorder_level: 5,
                    is_public: false,
                  })
                }
                className="inline-flex items-center space-x-1 px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs font-semibold hover:bg-purple-100"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ New Accessory</span>
              </button>
            </div>
          </div>

          {/* ── Laptop Batch Rows ─────────────────────────────────────────── */}
          {laptopBatchesFieldArray.fields.map((field, bIdx) => {
            const currentBatch = watchBatches[bIdx] || {};
            const batchQty = Number(currentBatch.quantity) || 1;
            const batchCost = Number(currentBatch.unit_cost) || 0;
            const batchLineTotal = batchQty * batchCost;
            const unitsList = currentBatch.units || [];
            const enteredSerialsCount = unitsList.filter((u) => u.serial_number?.trim() || u.service_tag?.trim()).length;
            const isExpanded = !!expandedBatches[bIdx];
            const warningMsg = batchWarnings[bIdx];

            return (
              <div key={field.id} className="p-3.5 bg-white rounded-lg border border-brand-border space-y-3 shadow-sm">
                {/* Batch Card Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 border-b border-brand-border pb-2">
                  <div className="flex items-center space-x-2">
                    <span className="p-1.5 bg-green-50 text-brand-green rounded border border-green-200">
                      <Layers className="w-4 h-4" />
                    </span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-brand-black">
                          {currentBatch.brand || 'Laptop'} {currentBatch.model || 'Batch'}
                        </span>
                        {currentBatch.raw_description && (
                          <span className="text-[10px] bg-brand-bg px-2 py-0.5 rounded text-brand-muted border border-brand-border truncate max-w-xs" title={currentBatch.raw_description}>
                            Ref: {currentBatch.raw_description}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-brand-muted mt-0.5">
                        {[currentBatch.cpu, currentBatch.ram, currentBatch.storage].filter(Boolean).join(' | ') || 'Specs pending review'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <span className="text-[10px] text-brand-muted uppercase font-bold block">Batch Total</span>
                      <span className="text-xs font-extrabold text-brand-green">
                        Rs. {batchLineTotal.toLocaleString('en-PK')}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => laptopBatchesFieldArray.remove(bIdx)}
                      className="text-brand-red hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Remove Batch"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Batch Conflict / Notice Warning */}
                {warningMsg && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900 flex items-center justify-between">
                    <div className="flex items-center space-x-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      <span>{warningMsg}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBatchWarnings((prev) => ({ ...prev, [bIdx]: '' }))}
                      className="text-amber-700 hover:text-amber-900 font-bold ml-2"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Batch Fields Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Brand *</label>
                    <input {...register(`laptop_batches.${bIdx}.brand`)} placeholder="e.g. HP, Dell" className={inputClass(!!errors.laptop_batches?.[bIdx]?.brand)} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Model *</label>
                    <input {...register(`laptop_batches.${bIdx}.model`)} placeholder="e.g. 840 G6" className={inputClass(!!errors.laptop_batches?.[bIdx]?.model)} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">CPU</label>
                    <input {...register(`laptop_batches.${bIdx}.cpu`)} placeholder="e.g. Core i5 8th Gen" className={inputClass()} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">RAM & Storage</label>
                    <div className="flex gap-1">
                      <input {...register(`laptop_batches.${bIdx}.ram`)} placeholder="16GB" className={inputClass()} />
                      <input {...register(`laptop_batches.${bIdx}.storage`)} placeholder="256GB SSD" className={inputClass()} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Touchscreen</label>
                    <select
                      value={watchBatches[bIdx]?.touchscreen === true ? 'true' : watchBatches[bIdx]?.touchscreen === false ? 'false' : ''}
                      onChange={(e) => setValue(`laptop_batches.${bIdx}.touchscreen`, e.target.value === 'true' ? true : e.target.value === 'false' ? false : null, { shouldDirty: true })}
                      className={inputClass()}
                    >
                      <option value="">Unknown</option>
                      <option value="true">Touchscreen</option>
                      <option value="false">Non-Touch</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Condition *</label>
                    <select {...register(`laptop_batches.${bIdx}.condition`)} className={inputClass()}>
                      <option value="new">New</option>
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      {...register(`laptop_batches.${bIdx}.quantity`)}
                      onChange={(e) => handleQuantityChange(bIdx, e.target.value)}
                      className={inputClass(!!errors.laptop_batches?.[bIdx]?.quantity)}
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Unit Cost (PKR) *</label>
                    <input {...register(`laptop_batches.${bIdx}.unit_cost`)} type="number" placeholder="Cost" className={inputClass(!!errors.laptop_batches?.[bIdx]?.unit_cost)} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Asking Price (PKR) <span className="text-amber-500">(set before sale)</span></label>
                    <input
                      {...register(`laptop_batches.${bIdx}.selling_price`)}
                      type="number"
                      placeholder="Asking (0 = TBD)"
                      className={inputClass(!!errors.laptop_batches?.[bIdx]?.selling_price) +
                        (watch(`laptop_batches.${bIdx}.selling_price`) == 0 ? ' border-amber-400 bg-amber-50' : '')
                      }
                    />
                    {watch(`laptop_batches.${bIdx}.selling_price`) == 0 && (
                      <p className="text-[9px] text-amber-600 mt-0.5">Price is 0 — set before putting on sale.</p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-[9px] font-bold text-brand-muted uppercase mb-0.5">Min Price (PKR)</label>
                    <input {...register(`laptop_batches.${bIdx}.minimum_price`)} type="number" placeholder="Min" className={inputClass()} />
                  </div>
                </div>

                {/* ── Expandable Unit Serials Section ─────────────────────────── */}
                <div className="pt-2 border-t border-brand-border">
                  <button
                    type="button"
                    onClick={() => toggleBatchExpand(bIdx)}
                    className="w-full flex items-center justify-between px-3 py-1.5 bg-brand-bg hover:bg-gray-100 rounded text-xs font-semibold text-brand-black transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Barcode className="w-3.5 h-3.5 text-brand-green" />
                      <span>Unit Details / Serials ({enteredSerialsCount} of {batchQty} entered)</span>
                      <span className="text-[10px] text-brand-muted font-normal">(Optional before receiving)</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-brand-muted" /> : <ChevronDown className="w-4 h-4 text-brand-muted" />}
                  </button>

                  {isExpanded && (
                    <div className="mt-2 space-y-2 pl-1 pr-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                        {Array.from({ length: batchQty }).map((_, uIdx) => (
                          <div key={uIdx} className="p-2 bg-brand-bg rounded border border-brand-border flex items-center space-x-2 text-xs">
                            <span className="font-bold text-[10px] text-brand-muted shrink-0 w-6">#{uIdx + 1}</span>
                            <div className="flex-1 grid grid-cols-2 gap-1.5">
                              <input
                                {...register(`laptop_batches.${bIdx}.units.${uIdx}.serial_number`)}
                                placeholder="Serial / S/N"
                                className="px-2 py-1 bg-white border border-brand-border rounded text-[11px] text-brand-black focus:outline-none focus:ring-1 focus:ring-brand-green"
                              />
                              <input
                                {...register(`laptop_batches.${bIdx}.units.${uIdx}.service_tag`)}
                                placeholder="Service Tag"
                                className="px-2 py-1 bg-white border border-brand-border rounded text-[11px] text-brand-black focus:outline-none focus:ring-1 focus:ring-brand-green"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setActiveScanTarget({ batchIdx: bIdx, unitIdx: uIdx })}
                              className="p-1 bg-white border border-brand-green/30 text-brand-green hover:bg-brand-green hover:text-white rounded shrink-0 transition-colors"
                              title="Scan Specs for this unit"
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* ── Existing Accessory Rows ──────────────────────────────────── */}
          {existingAccFieldArray.fields.map((field, idx) => (
            <div key={field.id} className="p-3 bg-white rounded-lg border border-brand-border space-y-2">
              <div className="flex justify-between items-center border-b border-brand-border pb-1">
                <span className="text-xs font-bold text-blue-700 flex items-center space-x-1">
                  <Package className="w-3.5 h-3.5" />
                  <span>Existing Accessory #{idx + 1}</span>
                </span>
                <button type="button" onClick={() => existingAccFieldArray.remove(idx)} className="text-brand-red hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select {...register(`existing_accessory_items.${idx}.accessory_id`)} className={inputClass(!!errors.existing_accessory_items?.[idx]?.accessory_id)}>
                  <option value="">Select Accessory</option>
                  {accessoriesData?.data.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} ({acc.quantity} in stock)
                    </option>
                  ))}
                </select>
                <input {...register(`existing_accessory_items.${idx}.quantity`)} type="number" placeholder="Quantity *" className={inputClass()} />
                <input {...register(`existing_accessory_items.${idx}.unit_cost`)} type="number" placeholder="Unit Cost (PKR) *" className={inputClass()} />
              </div>
            </div>
          ))}

          {/* ── New Accessory Rows ───────────────────────────────────────── */}
          {newAccFieldArray.fields.map((field, idx) => (
            <div key={field.id} className="p-3 bg-white rounded-lg border border-brand-border space-y-2">
              <div className="flex justify-between items-center border-b border-brand-border pb-1">
                <span className="text-xs font-bold text-purple-700 flex items-center space-x-1">
                  <Package className="w-3.5 h-3.5" />
                  <span>New Accessory #{idx + 1}</span>
                </span>
                <button type="button" onClick={() => newAccFieldArray.remove(idx)} className="text-brand-red hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <input {...register(`new_accessory_items.${idx}.name`)} placeholder="Item Name *" className={inputClass(!!errors.new_accessory_items?.[idx]?.name)} />
                <select {...register(`new_accessory_items.${idx}.category`)} className={inputClass()}>
                  <option value="Charger & Power">Charger & Power</option>
                  <option value="RAM & Memory">RAM & Memory</option>
                  <option value="SSD & Storage">SSD & Storage</option>
                  <option value="Bag & Sleeve">Bag & Sleeve</option>
                  <option value="Mouse & Keyboard">Mouse & Keyboard</option>
                  <option value="Cable & Adapter">Cable & Adapter</option>
                  <option value="Screen & Spare Parts">Screen & Spare Parts</option>
                  <option value="Other Accessories">Other Accessories</option>
                </select>
                <input {...register(`new_accessory_items.${idx}.quantity`)} type="number" placeholder="Quantity *" className={inputClass()} />
                <input {...register(`new_accessory_items.${idx}.unit_cost`)} type="number" placeholder="Unit Cost (PKR) *" className={inputClass()} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
                <div>
                  <input
                    {...register(`new_accessory_items.${idx}.selling_price`)}
                    type="number"
                    placeholder="Selling Price (0 = TBD)"
                    className={inputClass(!!errors.new_accessory_items?.[idx]?.selling_price) +
                      (watch(`new_accessory_items.${idx}.selling_price`) == 0 ? ' border-amber-400 bg-amber-50' : '')
                    }
                  />
                  {watch(`new_accessory_items.${idx}.selling_price`) == 0 && (
                    <p className="text-[9px] text-amber-600 mt-0.5">Price is 0 — set before sale.</p>
                  )}
                </div>
                <input {...register(`new_accessory_items.${idx}.sku`)} placeholder="SKU / Barcode (Optional)" className={inputClass()} />
              </div>
            </div>
          ))}
        </div>

        {/* ── Additional Costs & Payment ──────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-brand-border">
          <div className="space-y-2 bg-brand-bg p-3 rounded-lg border border-brand-border">
            <h4 className="text-xs font-bold text-brand-black uppercase">Freight & Discount</h4>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-brand-muted uppercase">Shipping</label>
                <input {...register('shipping_cost')} type="number" placeholder="0" className={inputClass()} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-brand-muted uppercase">Other / Duty</label>
                <input {...register('other_cost')} type="number" placeholder="0" className={inputClass()} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-[10px] font-semibold text-brand-muted uppercase">Discount (PKR)</label>
                  <button
                    type="button"
                    onClick={() => {
                      const pct = prompt('Enter discount percentage (e.g. 5 for 5%):');
                      if (pct && !isNaN(Number(pct))) {
                        const calc = Math.round((subtotal * Number(pct)) / 100);
                        setValue('discount', calc);
                      }
                    }}
                    className="text-[9px] text-brand-green hover:underline font-semibold"
                    title="Calculate discount as % of subtotal"
                  >
                    % Calc
                  </button>
                </div>
                <input {...register('discount')} type="number" placeholder="0" className={inputClass(errors.discount !== undefined)} />
                {errors.discount && <p className="text-[10px] text-brand-red">{errors.discount.message}</p>}
              </div>
            </div>
          </div>

          <div className="space-y-2 bg-brand-bg p-3 rounded-lg border border-brand-border">
            <h4 className="text-xs font-bold text-brand-black uppercase">Initial Payment</h4>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-brand-muted uppercase">Amount Paid</label>
                <input {...register('amount_paid')} type="number" placeholder="0" className={inputClass()} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-brand-muted uppercase">Method</label>
                <select {...register('payment_method')} className={inputClass()}>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_wallet">Mobile Wallet</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── Summary Breakdown ────────────────────── */}
        <div className="p-3 bg-green-50/70 border border-brand-green/40 rounded-lg text-xs space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center sm:text-left">
            <div>
              <span className="text-[10px] font-semibold text-brand-muted uppercase block">Items Subtotal</span>
              <span className="font-bold text-brand-black">Rs. {subtotal.toLocaleString('en-PK')}</span>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-brand-muted uppercase block">Shipping / Other</span>
              <span className="font-semibold text-brand-black">Rs. {totalExtraCosts.toLocaleString('en-PK')}</span>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-brand-muted uppercase block">Purchase Discount</span>
              <span className="font-semibold text-amber-700">- Rs. {watchDiscount.toLocaleString('en-PK')}</span>
            </div>
            <div>
              <span className="text-[10px] font-semibold text-brand-muted uppercase block">Net Purchase Total</span>
              <span className="font-extrabold text-brand-green text-sm">Rs. {netPurchaseTotal.toLocaleString('en-PK')}</span>
            </div>
          </div>
          <div className="pt-2 border-t border-brand-green/20 flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-[11px]">
            <div>
              <span className="text-brand-muted">Amount Paid: </span>
              <span className="font-bold text-brand-black">Rs. {watchAmountPaid.toLocaleString('en-PK')}</span>
            </div>
            <div>
              <span className="text-brand-muted">Balance Due: </span>
              <span className={`font-bold ${balanceDue > 0 ? 'text-brand-red' : 'text-brand-green'}`}>
                Rs. {balanceDue.toLocaleString('en-PK')}
              </span>
            </div>
          </div>
        </div>

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button type="button" onClick={onCancel} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || suppliersLoading || !!suppliersError}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 shadow-sm"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{mutation.isPending ? 'Processing...' : 'Confirm & Receive Purchase'}</span>
          </button>
        </div>
      </form>

      {/* ── New Supplier Modal ──────────────────────────────────────────────── */}
      <Modal
        isOpen={showNewSupplierModal}
        onClose={() => setShowNewSupplierModal(false)}
        title="Add New Supplier"
        maxWidth="md"
      >
        <SupplierForm
          initialName={initialData?.extracted_supplier_name || ''}
          onSuccess={(created) => {
            setShowNewSupplierModal(false);
            if (created?.id) {
              setValue('supplier_id', created.id, { shouldValidate: true, shouldDirty: true });
            }
          }}
          onCancel={() => setShowNewSupplierModal(false)}
        />
      </Modal>

      {/* ── Unit Spec Scanner Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={activeScanTarget !== null}
        onClose={() => setActiveScanTarget(null)}
        title={`Scan Specs for Unit #${(activeScanTarget?.unitIdx ?? 0) + 1}`}
        maxWidth="md"
      >
        <LaptopSpecExtractorModal
          onConfirm={handleUnitSpecConfirmed}
          onCancel={() => setActiveScanTarget(null)}
        />
      </Modal>
    </>
  );
};
